import _ from 'lodash'
import { CONSTANTS } from '../../Constants'
import affirm from '../../core/Affirm'
import DevSettings from '../../helpers/DevSettings'
import Helper from '../../helpers/Helper'
import { store } from '../../store/Store'
import NEXUS_API_URLS from './NexusApiUrls'
import OS_API_URLS from './OsApiUrls'
import SETTLEMENT_API_URLS from './SettlementApiUrls'
import { COMPILED_CONSTANTS } from '../../CompiledConstants'
import CONTRACT_INGESTION_API_URLS from './ContractIngestionApiUrls'

const MODULES_MATRIX: ModuleMatrix = {
    os: {
        local: 'http://127.0.0.1:3001',
        prod: `${COMPILED_CONSTANTS.evolveApiProxyUrl}/${CONSTANTS.awsInputs.os.apiGatewayStageName}`,
        apiGatewayId: COMPILED_CONSTANTS.evolveOSApiGatewayId,
        urls: OS_API_URLS
    },
    settlement: {
        local: 'http://127.0.0.1:3002',
        prod: `${COMPILED_CONSTANTS.evolveApiProxyUrl}/${CONSTANTS.awsInputs.settlement.apiGatewayStageName}`,
        apiGatewayId: COMPILED_CONSTANTS.evolveSettlementApiGatewayId,
        urls: SETTLEMENT_API_URLS
    },
    nexus: {
        local: 'http://127.0.0.1:3003',
        prod: `${COMPILED_CONSTANTS.evolveApiProxyUrl}/${CONSTANTS.awsInputs.nexus.apiGatewayStageName}`,
        apiGatewayId: COMPILED_CONSTANTS.evolveNexusApiGatewayId,
        urls: NEXUS_API_URLS
    },
    contractIngestion: {
        local: 'http://127.0.0.1:3004',
        prod: `${COMPILED_CONSTANTS.evolveApiProxyUrl}/${CONSTANTS.awsInputs.contractIngestion.apiGatewayStageName}`,
        apiGatewayId: COMPILED_CONSTANTS.evolveContractIngestionApiGatewayId,
        urls: CONTRACT_INGESTION_API_URLS
    }
}

class ApiUrlCompilerClass {
    /**
     * if you want to ovverride the default behaviour of getting and URL (which is in dev gets "local" and in production get "prod")
     * then upsert the file local.json by adding a key of value "api-[name of the module (either os, settlement or nexus)]" and the key of the url that you want to use
     * e.g. "api-os": "forza" -> if the url requested is from the os module, then the url that will be used is the one under MODULES_MATRIX.os.forza, the others will work the same
     * e.g. "api-settlement": "prod" -> if the url requested is from the settlement module, then the url that will be used is the one under MODULES_MATRIX.settlement.prod, the others will work the same
     */
    compile = (url: string) => {
        affirm(url, 'Invalid url')

        return { url: this.resolve(url), headers: this.headers(url) }
    }

    resolve = (url: string): string => {
        affirm(url, 'Invalid url')

        const { module, moduleKey } = this.parse(url)

        if (!Helper.isDev()) return module.prod + url
        else {
            const root = module[DevSettings.getValue(`api-${moduleKey}`)]
            console.log('root', root)
            if (root) return root + url
            else return module.local + url
        }
    }

    headers = (url: string): HeadersInit => {
        affirm(url, 'Invalid url')

        const jwt = store.getState()?.session?.JWT
        const { moduleKey, module } = this.parse(url)

        const liveHeader: HeadersInit = [
            ['Authorization', jwt],
            ['x-apigw-api-id', module.apiGatewayId]
        ]

        if (!Helper.isDev()) return liveHeader

        const headers = DevSettings.getValue(`api-${moduleKey}-headers`) as [string, string][]

        if (Helper.hasVal(headers) && headers.length > 0) {
            affirm(headers.every(x => x.length === 2), `Wrong headers configuration: all headers should be composed of 2 value tuples [string, string], but received ${JSON.stringify(headers)}`)
            const compiledHeaders = _.cloneDeep(headers)

            for (let i = 0; i < compiledHeaders.length; i++) {
                const header = compiledHeaders[i]
                if (header[1].toLowerCase() === '${jwt}') {
                    compiledHeaders[i][1] = jwt
                }
            }

            return compiledHeaders
        } else {
            // Now we need JWT also in local
            // const env = DevSettings.getValue(`api-${moduleKey}`)
            // if (env === 'local' || !Helper.hasVal(env)) return []

            return liveHeader
        }
    }

    private parse = (url: string) => {
        affirm(url, 'Invalid url')

        const modules = Object.keys(MODULES_MATRIX) as ModuleType[]
        const moduleKey = modules.find(x => this.isUrlInModule(url, MODULES_MATRIX[x]))
        const module = MODULES_MATRIX[moduleKey]

        affirm(module, `Requested url doesn't exist in any module: "${url}"`)

        return { module, moduleKey }
    }

    private isUrlInModule = (url: string, moduleItem: IModuleMatryxItem) => {
        return Object.keys(moduleItem.urls).map(k => moduleItem.urls[k]).includes(url)
    }
}

const ApiUrlCompiler = new ApiUrlCompilerClass()
export default ApiUrlCompiler

export enum ModuleType {
    os = 'os',
    settlement = 'settlement',
    nexus = 'nexus',
    contractIngestion = 'contractIngestion'
}

export type ModuleMatrix = {
    [key in ModuleType]: IModuleMatryxItem
}

export interface IModuleMatryxItem {
    local: string
    prod: string
    apiGatewayId: string
    urls: { [key: string]: string }
}
